import React, { CSSProperties, useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import ScrollLock from 'components/Common/ScrollLock'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import MultipleMarkers from 'components/Common/PropertyMap/MultipleMarkers'
import HideLiveChat from 'components/Common/HideLiveChat'
import HeadData from 'components/Common/HeadData'
import OfferListFrontPageFilters from 'components/OfferList/OfferListFilter/OfferListFrontPageFilters'
import PaneBody from 'components/Common/Pane/PaneBody'
import Modal from 'components/Luxkit/Modal/Modal'
import FrontPageTile from 'components/OfferList/OfferListTiles/FrontPageOfferTiles/FrontPageTile'
import {
  productClickViewDetails,
  mapPropertyPinClick,
  mapOfferInfoClick,
  mapPinOfferView,
} from 'actions/AnalyticsActions'
import { groupOffersGeographically } from 'components/Common/PropertyMap/utils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import useToggle from 'hooks/useToggle'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import zIndex from 'styles/tools/z-index'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'

const Root = styled.div`
  display: flex;
  flex-direction: column;
`

const StickyHeader = styled(Pane)`
  top: 0;
  z-index: ${zIndex.offerFilters};
  position: sticky;
`

interface Props extends RouteComponentProps<any> {
  urlFilters: App.OfferListFilters;
  filters?: App.OfferListFilters;
  listViewPath: string;
  offerTypes: Array<App.LEContractedOfferType>;
  heading?: React.ReactNode;
  onPropertyPinClick: (...params: Parameters<typeof mapPropertyPinClick>) => void;
  onPinOfferView:(...params: Parameters<typeof mapPinOfferView>) => void;
  mapOfferInfoClick: (...params: Parameters<typeof mapOfferInfoClick>) => void;
  productClickViewDetails: (...params: Parameters<typeof productClickViewDetails>) => void;
}

function MapViewPage(props: Props) {
  const {
    urlFilters,
    filters,
    offerTypes,
    listViewPath,
    mapOfferInfoClick,
    productClickViewDetails,
    heading,
  } = props

  const [mobileInterface, setMobileInterface] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<App.Offer | App.OfferSummary>(undefined)
  const { value: showingSelectOffer, on: showSelectOffer, off: hideSelectOffer } = useToggle(false)

  const finalFilters = useMemo((): App.OfferListFilters => ({
    ...urlFilters,
    ...filters,
    offerTypes,
  }), [urlFilters, filters, offerTypes])

  const offerList = useOfferList(finalFilters)
  const [offers] = useOffers<App.Offer | App.OfferSummary>(offerList.offerIds)

  const mapOffers = groupOffersGeographically(offers)

  const onUpdateDisplay = useCallback((mapOptions) => {
    setMobileInterface(mapOptions.mobileInterface)
    setLoaded(true)
  }, [])

  const onSelectPropertyMarker = useCallback((marker: App.MapMarker) => {
    if (marker.type === PROPERTY_MAP_MARKER_TYPE) {
      const offer = offers.find(offer => offer.id === marker.propertyItem.offerId)
      setSelectedOffer(offer)
      showSelectOffer()
    }
  }, [offers, showSelectOffer])

  const mapOfferClick = useCallback((offer: App.Offer) => {
    mapOfferInfoClick(offer)
    productClickViewDetails(offer)
  }, [mapOfferInfoClick, productClickViewDetails])

  const { heights: { site: siteHeaderHeight, siteStickyBanner } } = useContext(HeaderHeightContext)
  const rootStyle = useMemo<CSSProperties>(() => ({ height: `calc(100vh - ${siteHeaderHeight + siteStickyBanner + 1}px)` }), [siteHeaderHeight, siteStickyBanner])

  // Height to at least 1px to make google maps script load
  const wrapperStyle = { flexGrow: 1, height: '1px', display: 'flex', flexDirection: 'column' }
  const mobileCarousel = !offerList.fetching && mobileInterface

  return (<Root style={rootStyle}>
    <HeadData noIndex />
    <StickyHeader shadow>
      <LayoutContainer>
        <PaneBody size="md">
          <OfferListFrontPageFilters
            heading={heading}
            types={offerTypes}
            existingFilters={finalFilters}
            listViewPath={listViewPath}
          />
        </PaneBody>
      </LayoutContainer>
    </StickyHeader>
    <MultipleMarkers
      wrapperStyle={wrapperStyle}
      height="100%"
      offers={mapOffers}
      mobileInterface={mobileInterface}
      onUpdateDisplay={onUpdateDisplay}
      onSelectPropertyMarker={onSelectPropertyMarker}
      loaded={!offerList.fetching}
      markerOptions={{
        xOffset: 0,
        yOffset: mobileCarousel ? 100 : 0,
      }}
    />

    <Modal
      title=""
      isOpen={showingSelectOffer && selectedOffer && mobileCarousel}
      onClose={hideSelectOffer}
    >
      {selectedOffer && <FrontPageTile
        offer={selectedOffer}
        eagerLoadFirstImage
        productClick={mapOfferClick}
      />}
    </Modal>

    {loaded && <ScrollLock />}
    <HideLiveChat />
  </Root>)
}

MapViewPage.defaultProps = {
  offerTypes: FLASH_OFFERS_TYPES,
}

const mapStateToProps = (state: App.State) => ({
  regionCode: state.geo.currentRegionCode,
  currencyCode: state.geo.currentCurrency,
  urlFilters: getUrlOfferListFilters(state),
})

const mapDispatchToProps = {
  productClickViewDetails,
  onPropertyPinClick: mapPropertyPinClick,
  mapOfferInfoClick,
  onPinOfferView: mapPinOfferView,
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MapViewPage))
